import { useRef, useEffect, useState } from 'react';
import './EventsSidebar.css';

export default function EventsSidebar({
  events = [],
  activeEventIndex = null,
  onEventClick,
  className = '',
}) {
  const eventRefs = useRef({});
  const [expandedEvents, setExpandedEvents] = useState({});

  useEffect(() => {
    if (activeEventIndex !== null && eventRefs.current[activeEventIndex]) {
      eventRefs.current[activeEventIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, [activeEventIndex]);

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const actionEvents = events.filter(e => e.type === 'action' || e.videoOffsetMs !== undefined);

  const skeletonTitleWidths = ['88%', '72%', '92%', '64%', '80%'];

  return (
    <div className={`zibby-events-sidebar ${className}`.trim()}>
      <div className="zibby-events-header">
        <h3>Events ({actionEvents.length})</h3>
      </div>
      
      <div className="zibby-events-list">
        {actionEvents.length === 0 ? (
          <>
            <p className="zibby-events-sr-only">
              No events yet. Timeline steps will appear here when the run produces them.
            </p>
            {skeletonTitleWidths.map((w, k) => (
              <div
                key={`event-skeleton-${k}`}
                className="zibby-event-item zibby-event-skeleton"
                aria-hidden
              >
                <div className="zibby-event-main-row">
                  <div className="zibby-event-skeleton-num" />
                  <div className="zibby-event-content">
                    <div className="zibby-event-tool-wrapper">
                      <div
                        className="zibby-event-skeleton-line zibby-event-skeleton-line--title"
                        style={{ width: w }}
                      />
                    </div>
                    <div className="zibby-event-skeleton-line zibby-event-skeleton-line--time" />
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : null}
        {actionEvents.map((event, i) => {
          // Check if event has data to expand (either data.params or data object with properties)
          const hasParams = event.data && (event.data.params || Object.keys(event.data).length > 0);
          const isExpanded = expandedEvents[i];
          const isEvidence = event.isEvidence;
          const verdict = event.verdict;
          const description = event.description;
          
          return (
            <div
              key={i}
              ref={el => eventRefs.current[i] = el}
              className={`zibby-event-item ${i === activeEventIndex ? 'zibby-event-active' : ''} ${isEvidence ? `zibby-event-evidence zibby-evidence-${verdict}` : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onEventClick) {
                  onEventClick(event, i);
                }
              }}
              style={{ pointerEvents: 'auto' }}
            >
              <div 
                className="zibby-event-main-row"
              >
                <div className="zibby-event-number">{i + 1}</div>
                <div className="zibby-event-content">
                  <div className="zibby-event-tool-wrapper">
                    <div
                      className="zibby-event-tool"
                      title={description || event.type || 'Unknown'}
                    >
                      {description || event.type || 'Unknown'}
                    </div>
                    {isEvidence && (
                      <span className={`zibby-evidence-badge zibby-badge-${verdict}`}>
                        {verdict === 'pass' ? '✓ PASS' : '✗ FAIL'}
                      </span>
                    )}
                  </div>
                  <div className="zibby-event-time">{formatTime(event.videoOffsetMs || 0)}</div>
                </div>
              </div>
              {hasParams && (
                <button 
                  className="zibby-event-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedEvents(prev => ({...prev, [i]: !prev[i]}));
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              {hasParams && isExpanded && (
                <div className="zibby-event-params">
                  <pre>{JSON.stringify(event.data.params || event.data, null, 2)}</pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

